import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { Options, V2AdminUpdateBlogPostData } from '@workspace/api-backend'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { Trash2 } from 'lucide-react'
import { useState } from 'react'
import {
  useAdminBlogPostByIdQuery,
  useAdminDeleteBlogPostMutation,
  useAdminUpdateBlogPostMutation,
} from '../../../features/admin/hooks/use-admin-api'
import { BlogForm, type BlogFormData } from '../../../features/blog/components/blog-form'

export const Route = createFileRoute('/admin/blog/$id')({
  component: Page,
})

function Page() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [saveMessage, setSaveMessage] = useState<{
    type: 'success' | 'error'
    text: string
  } | null>(null)

  const postQuery = useAdminBlogPostByIdQuery(id)
  const updateMutation = useAdminUpdateBlogPostMutation(id)
  const deleteMutation = useAdminDeleteBlogPostMutation()

  const post = postQuery.data?.data

  if (postQuery.isLoading) {
    return (
      <div className="flex h-48 items-center justify-center text-muted-foreground">Loading...</div>
    )
  }

  if (!post) {
    return (
      <div className="flex h-48 items-center justify-center text-muted-foreground">
        Blog post not found
      </div>
    )
  }

  const showTemporaryMessage = (type: 'success' | 'error', text: string) => {
    setSaveMessage({ type, text })
    setTimeout(() => setSaveMessage(null), 3000)
  }

  const handleSubmit = (data: BlogFormData) => {
    updateMutation.mutate(
      {
        path: { id },
        body: {
          title: data.title,
          slug: data.slug || undefined,
          content: data.content,
          excerpt: data.excerpt || null,
          metaDescription: data.metaDescription || null,
          coverImageUrl: data.coverImageUrl || null,
          tags: data.tags,
          targetQueries: data.targetQueries,
          status: data.status,
        },
      } as Options<V2AdminUpdateBlogPostData>,
      {
        onSuccess: () => showTemporaryMessage('success', 'Saved'),
        onError: () => showTemporaryMessage('error', 'Failed to save'),
      }
    )
  }

  const handleTogglePublish = () => {
    const newStatus = post.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED'
    updateMutation.mutate(
      {
        path: { id },
        body: { status: newStatus },
      } as Options<V2AdminUpdateBlogPostData>,
      {
        onSuccess: () =>
          showTemporaryMessage('success', newStatus === 'PUBLISHED' ? 'Published' : 'Unpublished'),
        onError: () => showTemporaryMessage('error', 'Failed to update status'),
      }
    )
  }

  const handleDelete = () => {
    deleteMutation.mutate(
      { path: { id } },
      {
        onSuccess: () => navigate({ to: '/admin/blog' }),
      }
    )
  }

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6">
      {/* Top bar with actions */}
      <div className="flex items-center justify-between gap-4">
        <h1 className="min-w-0 truncate text-lg font-bold">{post.title}</h1>
        <div className="flex shrink-0 items-center gap-2">
          {showDeleteConfirm ? (
            <>
              <span className="text-sm text-red-400">Delete this post?</span>
              <SecondaryButton
                tone="danger"
                size="sm"
                onClick={handleDelete}
                loading={deleteMutation.isPending}
              >
                Confirm
              </SecondaryButton>
              <SecondaryButton size="sm" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </SecondaryButton>
            </>
          ) : (
            <>
              <SecondaryButton
                tone="danger"
                size="sm"
                icon={<Trash2 />}
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete
              </SecondaryButton>
              <SecondaryButton
                size="sm"
                onClick={handleTogglePublish}
                loading={updateMutation.isPending}
              >
                {post.status === 'PUBLISHED' ? 'Unpublish' : 'Publish'}
              </SecondaryButton>
              <CtaButton
                size="sm"
                type="submit"
                form="blog-edit-form"
                loading={updateMutation.isPending}
              >
                Save
              </CtaButton>
            </>
          )}
        </div>
      </div>

      <BlogForm
        formId="blog-edit-form"
        initialData={post}
        onSubmit={handleSubmit}
        isSubmitting={updateMutation.isPending}
      />

      {saveMessage && (
        <div
          className={`fixed bottom-6 right-6 rounded-lg px-4 py-2.5 text-sm font-medium shadow-lg ${
            saveMessage.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}
        >
          {saveMessage.text}
        </div>
      )}
    </div>
  )
}
